<template>
  <div class="trading-stats" v-if="cubes.length > 0">
    <h2 v-if="headline" class="trading-stats__headline">{{ headline }}</h2>
    <ul class="trading-stats__list">
      <li
        v-for="cube in cubes"
        :key="cube.cubeName"
        class="trading-stats__list-item"
      >
        <div class="trading-stats__cube">
          <div class="trading-stats__nickname">
            {{ cube.nickName }}
          </div>
        </div>
        <ConsumptionBar
          :extra-classes="['trading-stats__bar']"
          :bar-classes="barValueClasses(cube)"
          :value="cube.value / valueSum"
        />
        <div class="trading-stats__value">
          {{ formatKWH(cube.value * 1000) }}
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
  import ConsumptionBar from '@/components/ConsumptionBar.vue';
  import { useUnits } from '@/composables/useUnits';
  import { MIN_ENERGY_TRACE } from '@/constants';
  import { useHistoryStore } from '@/stores/useHistoryStore';

  export default {
    name: 'trading-statistics',
    props: {
      dataSource: {
        type: String,
        required: true,
      },
      headline: {
        type: String,
      },
      energyType: {
        type: String,
      },
      isProducers: {
        type: Boolean,
        required: true,
      },
    },
    components: {
      ConsumptionBar,
    },
    setup() {

      const historyStore = useHistoryStore()
      const { formatKWH } = useUnits()
      return {
        historyStore,
        formatKWH,
      }
    },
    computed: {
      cubes() {
        const raw = this.historyStore.getTradingStatistics(this.dataSource)
        let result = raw.data
          .map((value, index) => {
            return {
              value: value,
              nickName: raw.labels[index],
              cubeName: raw.raw[index].cubeName,
              type: this.cubeEnergyType(raw.raw[index]),
            }
          })
          .filter(cube => cube.value >= MIN_ENERGY_TRACE)
          .sort((a, b) => {
            if (a.value > b.value) {
              return -1
            }
            if (a.value < b.value) {
              return 1
            }
            return 0
          })
        return result
      },
      valueSum() {
        return this.cubes.reduce((sum, cube) => {
          return cube.value + sum
        }, 0)
      },
    },
    methods: {
      cubeEnergyType(cube) {
        const type = cube.energyType || cube.productionType || cube.type
        return type ? type.toLowerCase() : false
      },
      barValueClasses(cube) {
        let classes = ['trading-stats__bar-value']
        const type = this.isProducers ? cube.type : this.energyType
        classes.push(`trading-stats__bar-value--${type}`)
        return classes
      },
      barValueStyles(cube) {
        const percent = ((cube.value / this.valueSum) * 100).toFixed(2)
        return `width: ${percent}%;`
      },
    },
  }
</script>

<style lang="scss">
  .trading-stats {
    margin: $spacer10 0 $spacer20;

    &:first-child {
      margin-top: 0;
    }

    @media (min-width: $bp-sm) {
      margin-top: $spacer30;

      &:first-child {
        margin-top: 0;
      }
    }

    &__headline {
      margin-bottom: $spacer05;
    }

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;

      &-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-bottom: $spacer025;
      }
    }

    &__cube {
      flex-basis: 11rem;
      flex-grow: 0;
      flex-shrink: 0;
      display: flex;
      flex-wrap: nowrap;
      justify-content: flex-start;
      align-items: center;
      font-size: $font-size-sm;

      @media (min-width: $bp-sm) {
        font-size: $font-size-base;
      }
    }

    &__icon {
      margin-right: $spacer05;
    }

    &__nickname {
      line-height: $line-height-min;
    }

    &__bar {
      display: none;
      flex-shrink: 0;
      flex-grow: 1;
      background: rgba(0, 0, 0, 0.03);
      margin-left: auto;
      max-width: 500px;
      margin-right: $spacer10;

      @media (min-width: $bp-ssm) {
        display: flex;
        justify-content: flex-start;
      }

      &-value {
        height: 1rem;

        &--pv,
        &--battery {
          background-color: $color-energy-pv;
        }

        &--water {
          background-color: $color-energy-water;
        }

        &--wind {
          background-color: $color-energy-wind;
        }

        &--biogas {
          background-color: $color-energy-biogas;
        }

        &--supplier {
          background-color: $color-energy-supplier;
        }

        &--unknown {
          background-color: $color-energy-unknown;
        }
      }
    }

    &__value {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: 6rem;
      text-align: right;

      @media (max-width: ($bp-ssm - 1px)) {
        margin-left: auto;
      }
    }
  }
</style>
